import { forwardRef, useContext, useImperativeHandle, useState } from "react";
import {
  MdOutlineSave,
  MdAdd,
  MdClose,
  MdOutlineExpandMore,
  MdUploadFile,
  MdOutlineSupervisedUserCircle,
  MdPersonAddAlt,
} from "react-icons/md";
import { AnimatePresence } from "framer-motion";
import { DropdownItemProps } from "../components/dropdown-menu/DropdownItem";
import DropdownMenuPopUp from "../pop-up-panels/dropdown-menu-pop-up";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import { singlify } from "utils/utils";

interface ClassesHeaderOptionsProps {
  ref: any;
  classesRef: React.MutableRefObject<any>;
}

const ClassesHeaderOptions: React.FC<ClassesHeaderOptionsProps> = forwardRef(
  ({ classesRef }, ref) => {
    // States definition
    const [showCreateNewButton, setShowCreateNewButton] = useState(true);
    const [saveButton, setSaveButton] = useState<{
      show: boolean;
      disabled: boolean;
    }>({ show: false, disabled: true });
    const [showCreateNewPopUp, setShowCreateNewPopUp] = useState(false);

    // Get the values from the context
    const { classesTerminology, studentsTerminology } = usePlatform();
    const { userRole } = useContext(MainContext);

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      setShowCreateNewButton(bool: boolean) {
        setShowCreateNewButton(bool);
      },
      setSaveButton(options: { show: boolean; disabled: boolean }) {
        setSaveButton(options);
      },
      setShowCreateNewPopUp(bool: boolean) {
        setShowCreateNewPopUp(bool);
      },
    }));

    /**
     * Defining the options for the create button dropdown list
     */
    const createNewOptions: DropdownItemProps[] = [
      {
        text: singlify(classesTerminology),
        icon: <MdOutlineSupervisedUserCircle className="w-full h-full" />,
        onClickFunction: () => onCreateClass(),
      },
      {
        text: singlify(studentsTerminology),
        icon: <MdPersonAddAlt className="w-full h-full" />,
        onClickFunction: () => onCreateEditStudent(),
      },
    ];

    /**
     * Function that executes the create class
     * defined on class/index.tsx
     */
    const onCreateClass = () => {
      // Execute the function
      classesRef.current.onCreateClass();
    };

    /**
     * Function that executes the create/edit
     * student function defined on class/index.tsx
     */
    const onCreateEditStudent = () => {
      // Execute the function
      classesRef.current.onCreateEditStudent();
    };

    /**
     * Function that executes the discard function
     * defined on class/index.tsx
     */
    const onDiscard = () => {
      // Execute the function
      classesRef.current.onDiscard();
    };

    /**
     * Function that opens the bulk import panel
     * defined on class/index.tsx
     */
    const onBulkImport = () => {
      classesRef.current.setShowImportStudents(true);
    };

    /**
     * Function that executes the save new student function
     * defined on class/index.tsx
     */
    const handleSave = () => {
      // If the save button is available, then make it work
      if (!saveButton.disabled) classesRef.current.handleSaveNewStudent();
    };

    return (
      <>
        {["master admin", "customer admin", "class admin"].includes(userRole) &&
          showCreateNewButton && (
            <>
              <div className="h-fit p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex">
                <div
                  className="p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex cursor-pointer"
                  onClick={onBulkImport}
                >
                  <div className="w-6 h-6 relative">
                    <MdUploadFile className="w-full h-full fill-secondary-600" />
                  </div>
                </div>
                <div className="justify-center items-center gap-0.5 inline-flex">
                  <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide select-none">
                    Bulk Import
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex relative">
                <div
                  className="justify-center items-center gap-0.5 inline-flex cursor-pointer"
                  onClick={() => {
                    if (!showCreateNewPopUp)
                      setShowCreateNewPopUp(!showCreateNewPopUp);
                  }}
                >
                  <div className="p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex">
                    <div className="w-6 h-6 relative">
                      <MdAdd className="w-full h-full fill-secondary-600" />
                    </div>
                  </div>
                  <div className="w-4 h-4 relative">
                    <MdOutlineExpandMore className="w-4 h-4 text-secondary-600" />
                  </div>
                </div>
                <div className="justify-center items-center gap-0.5 inline-flex">
                  <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap select-none">
                    Create
                  </div>
                  <div className="w-4 h-4 px-[4.28px] opacity-0 justify-center items-center flex" />
                </div>
                <AnimatePresence>
                  {showCreateNewPopUp && (
                    <DropdownMenuPopUp
                      containerStyle="absolute w-[154px] top-[90px] right-2 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
                      optionsList={createNewOptions}
                      setShowPopUp={setShowCreateNewPopUp}
                    />
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
        {saveButton.show && (
          <div className="justify-start items-start gap-1 flex">
            <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex">
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div
                  className="p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex cursor-pointer hover:bg-neutral-100 ease-in-out duration-300"
                  onClick={onDiscard}
                >
                  <div className="w-6 h-6 relative">
                    <MdClose className="w-full h-full fill-secondary-600" />
                  </div>
                </div>
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide">
                  Discard
                </div>
              </div>
            </div>
            <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex">
              <div
                className={
                  saveButton.disabled
                    ? "justify-center items-center gap-0.5 inline-flex"
                    : "justify-center items-center gap-0.5 inline-flex cursor-pointer"
                }
                onClick={handleSave}
              >
                <div
                  className={
                    saveButton.disabled
                      ? "p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex"
                      : "p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex hover:bg-neutral-100 ease-in-out duration-300"
                  }
                >
                  <div className="w-6 h-6 relative">
                    <MdOutlineSave
                      className={
                        saveButton.disabled
                          ? "w-full h-full fill-neutral-300"
                          : "w-full h-full fill-secondary-600"
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div
                  className={
                    saveButton.disabled
                      ? "text-center text-neutral-300 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap"
                      : "text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap"
                  }
                >
                  Save
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

export default ClassesHeaderOptions;
